/**
 * ログイン画面の全面モーション: 「操作を記録して、再生する」を1本のループで見せる。
 *
 * 粒子がWebページ(ブラウザ枠・ナビ・入力欄・ボタン・料金カード)を組み、その上で
 *   記録(REC) → 巻き戻し(REWIND) → 再生(REPLAY) → 発見(INSIGHT)
 * を繰り返す。記録中に見ている人がページ上でポインタを動かすと、その操作が記録され
 * そのまま再生される。記録はメモリ上だけで、どこにも送らない。
 */

export type ReelPhase =
  | "form"
  | "rec"
  | "rewind"
  | "replay"
  | "insight"
  | "agent"
  | "verify"
  | "dissolve";

export type ReelTerminalTone = "prompt" | "call" | "result" | "bad" | "good" | "dim";
export type ReelTerminalLine = { at: number; text: string; tone: ReelTerminalTone };

export type ReelLabels = {
  url: string;
  nav: [string, string, string];
  cta: string;
  toggle: string;
  plans: [string, string, string];
  clickPricing: string;
  scroll: string;
  inputMasked: string;
  rage: string;
  conversion: string;
  click: string;
  recording: string;
  you: string;
  visitor: string;
  rewind: string;
  replay: string;
  insightRage: string;
  insightConversion: string;
  /** エージェントの段で出すターミナルの題名と行。at はその段の開始からのms。 */
  agentTitle: string;
  agentLines: ReelTerminalLine[];
  /** この時刻に、直す場所(料金切替)へ印を付ける。 */
  agentFixAt: number;
  verifyLines: ReelTerminalLine[];
  verifyCallout: string;
};

type EventKind = "click" | "scroll" | "input" | "rage" | "conversion";
type TapeEvent = { t: number; kind: EventKind; x: number; y: number; label?: string };
type Sample = { t: number; x: number; y: number };
type Rect = [number, number, number, number];

const PHASES: [ReelPhase, number][] = [
  ["form", 1300],
  ["rec", 5000],
  ["rewind", 1200],
  ["replay", 5000],
  ["insight", 2400],
  ["agent", 5000],
  ["verify", 3800],
  ["dissolve", 800],
];
export const REEL_LOOP_MS = PHASES.reduce((sum, [, ms]) => sum + ms, 0);
export const TAPE_MS = 5000;

const BG = "#08100c";
const WIPE = "rgba(8, 16, 12, 0.26)";
const PALETTE = ["#c8f06a", "#6fe0a8", "#2f9a68", "#e4f4d4"];
const PALETTE_WEIGHTS = [0.26, 0.3, 0.3, 0.14];
const LIME = "#c8f06a";
const MINT = "#6fe0a8";
const INK = "#e4f4d4";
const AMBER = "#f0b04a";
const RED = "#ff5a3c";
const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";
const SANS = 'Inter, "Hiragino Sans", ui-sans-serif, system-ui, sans-serif';
const TIMELINE_H = 58;

// ページ部品(ページ矩形に対する正規化座標)
const CHROME: Rect = [0, 0, 1, 0.075];
const URL_BAR: Rect = [0.12, 0.018, 0.4, 0.04];
const LOGO: Rect = [0.035, 0.11, 0.045, 0.06];
const NAV: Rect[] = [
  [0.6, 0.115, 0.1, 0.05],
  [0.715, 0.115, 0.1, 0.05],
  [0.83, 0.115, 0.1, 0.05],
];
const TITLE: Rect = [0.05, 0.25, 0.5, 0.06];
const LINES: Rect[] = [
  [0.05, 0.35, 0.56, 0.02],
  [0.05, 0.395, 0.42, 0.02],
];
const IMAGE: Rect = [0.68, 0.24, 0.27, 0.32];
const INPUT: Rect = [0.05, 0.48, 0.38, 0.08];
const CTA: Rect = [0.45, 0.48, 0.18, 0.08];
const CARDS: Rect[] = [
  [0.05, 0.65, 0.27, 0.29],
  [0.365, 0.65, 0.27, 0.29],
  [0.68, 0.65, 0.27, 0.29],
];
const TOGGLE: Rect = [0.405, 0.695, 0.19, 0.05];

const center = ([x, y, w, h]: Rect) => ({ x: x + w / 2, y: y + h / 2 });
const PRICING = center(NAV[1]);
const INPUT_AT = { x: 0.2, y: 0.52 };
const TOGGLE_AT = center(TOGGLE);
const CTA_AT = center(CTA);

// 架空の訪問者の動き(テープ時刻ms → ページ座標)
const SCRIPT: Sample[] = [
  { t: 0, x: 0.92, y: 0.95 },
  { t: 850, ...PRICING },
  { t: 1250, ...PRICING },
  { t: 1900, x: 0.62, y: 0.42 },
  { t: 2350, ...INPUT_AT },
  { t: 3150, x: 0.24, y: 0.53 },
  { t: 3650, ...TOGGLE_AT },
  { t: 4250, x: 0.505, y: 0.722 },
  { t: 4700, ...CTA_AT },
  { t: TAPE_MS, ...CTA_AT },
];

const easeOutCubic = (v: number) => 1 - (1 - v) ** 3;
const easeInOut = (v: number) => (v < 0.5 ? 4 * v * v * v : 1 - (-2 * v + 2) ** 3 / 2);
const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

const interpolate = (samples: Sample[], t: number): Sample | null => {
  if (samples.length === 0) return null;
  if (t <= samples[0].t) return samples[0];
  for (let i = 1; i < samples.length; i += 1) {
    if (samples[i].t >= t) {
      const a = samples[i - 1];
      const b = samples[i];
      const p = b.t === a.t ? 1 : (t - a.t) / (b.t - a.t);
      return { t, x: a.x + (b.x - a.x) * p, y: a.y + (b.y - a.y) * p };
    }
  }
  return samples[samples.length - 1];
};

const scripted = (t: number) => {
  for (let i = 1; i < SCRIPT.length; i += 1) {
    if (SCRIPT[i].t >= t) {
      const a = SCRIPT[i - 1];
      const b = SCRIPT[i];
      const p = easeInOut(b.t === a.t ? 1 : clamp01((t - a.t) / (b.t - a.t)));
      return { x: a.x + (b.x - a.x) * p, y: a.y + (b.y - a.y) * p };
    }
  }
  const last = SCRIPT[SCRIPT.length - 1];
  return { x: last.x, y: last.y };
};

const scriptedEvents = (labels: ReelLabels): TapeEvent[] => [
  { t: 900, kind: "click", ...PRICING, label: labels.clickPricing },
  { t: 1650, kind: "scroll", x: 0.62, y: 0.4, label: labels.scroll },
  { t: 2400, kind: "input", ...INPUT_AT, label: labels.inputMasked },
  { t: 3700, kind: "rage", ...TOGGLE_AT },
  { t: 3880, kind: "rage", ...TOGGLE_AT },
  { t: 4060, kind: "rage", ...TOGGLE_AT, label: labels.rage },
  { t: 4750, kind: "conversion", ...CTA_AT, label: labels.conversion },
];

const drawArrow = (ctx: CanvasRenderingContext2D, x: number, y: number, fill: string | null, stroke: string) => {
  ctx.beginPath();
  ctx.moveTo(x, y);
  ctx.lineTo(x, y + 17);
  ctx.lineTo(x + 4.5, y + 12.6);
  ctx.lineTo(x + 8, y + 20);
  ctx.lineTo(x + 11, y + 18.6);
  ctx.lineTo(x + 7.6, y + 11.6);
  ctx.lineTo(x + 13.5, y + 11.6);
  ctx.closePath();
  if (fill) {
    ctx.fillStyle = fill;
    ctx.fill();
  }
  ctx.strokeStyle = stroke;
  ctx.lineWidth = 1.3;
  ctx.stroke();
};

const pill = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number) => {
  ctx.beginPath();
  ctx.roundRect(x, y, w, h, 4);
};

export type LoginReelOptions = {
  canvas: HTMLCanvasElement;
  /** 残像を残さない重ね描き用(文字・カーソル・タイムライン・ヒートマップ)。 */
  overlay: HTMLCanvasElement;
  host: HTMLElement;
  frame: HTMLElement;
  labels: ReelLabels;
  onPhase?: (phase: ReelPhase) => void;
};

export const createLoginReel = (options: LoginReelOptions) => {
  const { canvas, overlay, host, frame, labels, onPhase } = options;
  const trails = canvas.getContext("2d", { alpha: false });
  const ctx = overlay.getContext("2d");
  if (!trails || !ctx) return { destroy: () => undefined };

  let width = 0;
  let height = 0;
  let count = 0;
  let xs = new Float32Array(0);
  let ys = new Float32Array(0);
  let vxs = new Float32Array(0);
  let vys = new Float32Array(0);
  let txs = new Float32Array(0);
  let tys = new Float32Array(0);
  let targeted = new Uint8Array(0);
  let colors = new Uint8Array(0);
  let sizes = new Float32Array(0);
  let box = { x: 0, y: 0, w: 1, h: 1 };
  let page = { x: 0, y: 0, w: 1, h: 1 };
  let loopStart = performance.now();
  let phase: ReelPhase = "form";
  let rafId = 0;
  let resizeFrame = 0;
  let running = false;

  // テープ(記録した内容)
  let samples: Sample[] = [];
  let events: TapeEvent[] = [];
  let userTook = false;
  let replayedUpTo = -1;
  let recT = 0;
  let verifyClicks = 0;
  const ripples: { x: number; y: number; born: number; color: string }[] = [];
  const chips: { x: number; y: number; born: number; text: string; color: string }[] = [];
  const heat: { x: number; y: number; strength: number; rgb: string }[] = [];
  const pointer = { x: -9999, y: -9999, active: false, lastMove: -99999 };

  const toPx = (nx: number, ny: number) => ({ x: page.x + nx * page.w, y: page.y + ny * page.h });
  const rectPx = ([x, y, w, h]: Rect) => ({ x: page.x + x * page.w, y: page.y + y * page.h, w: w * page.w, h: h * page.h });
  const has = (kind: EventKind) => events.some((event) => event.kind === kind);

  const pickColor = () => {
    let roll = Math.random();
    for (let i = 0; i < PALETTE_WEIGHTS.length; i += 1) {
      roll -= PALETTE_WEIGHTS[i];
      if (roll <= 0) return i;
    }
    return 0;
  };

  const allocate = (next: number) => {
    const prev = count;
    const grow = <T extends Float32Array | Uint8Array>(source: T, make: (n: number) => T) => {
      const target = make(next);
      target.set(source.subarray(0, Math.min(prev, next)));
      return target;
    };
    xs = grow(xs, (n) => new Float32Array(n));
    ys = grow(ys, (n) => new Float32Array(n));
    vxs = grow(vxs, (n) => new Float32Array(n));
    vys = grow(vys, (n) => new Float32Array(n));
    txs = grow(txs, (n) => new Float32Array(n));
    tys = grow(tys, (n) => new Float32Array(n));
    targeted = grow(targeted, (n) => new Uint8Array(n));
    colors = grow(colors, (n) => new Uint8Array(n));
    sizes = grow(sizes, (n) => new Float32Array(n));
    for (let i = prev; i < next; i += 1) {
      xs[i] = Math.random() * width;
      ys[i] = Math.random() * height;
      colors[i] = pickColor();
      sizes[i] = 1.1 + Math.random() * 1.3;
    }
    count = next;
  };

  // ページの線画をoffscreenに描き、その画素を粒子の行き先にする
  const samplePage = (budget: number) => {
    const off = document.createElement("canvas");
    off.width = Math.max(1, Math.floor(page.w));
    off.height = Math.max(1, Math.floor(page.h));
    const g = off.getContext("2d", { willReadFrequently: true });
    if (!g) return [];
    const r = ([x, y, w, h]: Rect) => [x * off.width, y * off.height, w * off.width, h * off.height] as const;
    g.fillStyle = "#fff";
    g.strokeStyle = "#fff";
    g.lineWidth = 2;
    g.strokeRect(1, 1, off.width - 2, off.height - 2);
    g.strokeRect(...r(CHROME));
    for (let i = 0; i < 3; i += 1) g.fillRect(off.width * (0.02 + i * 0.022), off.height * 0.03, 6, 6);
    g.strokeRect(...r(URL_BAR));
    g.fillRect(...r(LOGO));
    for (const item of NAV) g.strokeRect(...r(item));
    g.fillRect(...r(TITLE));
    for (const line of LINES) {
      const [x, y, w, h] = r(line);
      for (let dx = 0; dx < w; dx += 22) g.fillRect(x + dx, y, Math.min(17, w - dx), h);
    }
    const [ix, iy, iw, ih] = r(IMAGE);
    g.strokeRect(ix, iy, iw, ih);
    g.beginPath();
    g.moveTo(ix, iy + ih);
    g.lineTo(ix + iw * 0.4, iy + ih * 0.45);
    g.lineTo(ix + iw * 0.62, iy + ih * 0.7);
    g.lineTo(ix + iw * 0.78, iy + ih * 0.52);
    g.lineTo(ix + iw, iy + ih * 0.8);
    g.stroke();
    g.beginPath();
    g.arc(ix + iw * 0.76, iy + ih * 0.26, Math.min(iw, ih) * 0.09, 0, Math.PI * 2);
    g.stroke();
    g.strokeRect(...r(INPUT));
    g.strokeRect(...r(CTA));
    for (const card of CARDS) {
      const [x, y, w, h] = r(card);
      g.strokeRect(x, y, w, h);
      g.fillRect(x + w * 0.1, y + h * 0.5, w * 0.36, h * 0.12);
      for (let dx = 0; dx < w * 0.8; dx += 16) g.fillRect(x + w * 0.1 + dx, y + h * 0.76, 11, 4);
    }
    g.strokeRect(...r(TOGGLE));

    const { data } = g.getImageData(0, 0, off.width, off.height);
    const collect = (gap: number) => {
      const points: number[] = [];
      for (let y = 0; y < off.height; y += gap) {
        for (let x = 0; x < off.width; x += gap) {
          if (data[(Math.floor(y) * off.width + Math.floor(x)) * 4 + 3] > 120) points.push(x, y);
        }
      }
      return points;
    };
    let points = collect(3);
    if (points.length > 0) points = collect(Math.max(1.5, 3 * Math.sqrt(points.length / 2 / budget)));
    for (let i = points.length / 2 - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [points[i * 2], points[j * 2]] = [points[j * 2], points[i * 2]];
      [points[i * 2 + 1], points[j * 2 + 1]] = [points[j * 2 + 1], points[i * 2 + 1]];
    }
    return points;
  };

  const assignPage = () => {
    const points = samplePage(count * 0.78);
    const available = Math.min(points.length / 2, count);
    for (let i = 0; i < count; i += 1) {
      targeted[i] = i < available ? 1 : 0;
      if (i < available) {
        txs[i] = page.x + points[i * 2];
        tys[i] = page.y + points[i * 2 + 1];
      }
    }
  };

  const measure = () => {
    const rect = host.getBoundingClientRect();
    const frameRect = frame.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    width = rect.width;
    height = rect.height;
    for (const layer of [canvas, overlay]) {
      layer.width = Math.round(width * dpr);
      layer.height = Math.round(height * dpr);
    }
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    trails.setTransform(dpr, 0, 0, dpr, 0, 0);
    trails.fillStyle = BG;
    trails.fillRect(0, 0, width, height);
    box = {
      x: frameRect.left - rect.left,
      y: frameRect.top - rect.top,
      w: Math.max(1, frameRect.width),
      h: Math.max(1, frameRect.height),
    };
    page = { x: box.x, y: box.y, w: box.w, h: Math.max(1, box.h - TIMELINE_H) };
    const cap = width < 820 ? 1800 : 3600;
    allocate(Math.round(Math.min(cap, (width * height * 3.4) / 1000)));
    assignPage();
  };

  const phaseAt = (loopMs: number): [ReelPhase, number, number] => {
    let acc = 0;
    for (const [name, ms] of PHASES) {
      if (loopMs < acc + ms) return [name, loopMs - acc, ms];
      acc += ms;
    }
    return ["dissolve", 0, 1];
  };

  const emit = (event: TapeEvent, now: number, mode: "rec" | "replay") => {
    const at = toPx(event.x, event.y);
    const color = event.kind === "rage" ? RED : event.kind === "input" ? AMBER : mode === "rec" ? LIME : MINT;
    if (event.kind !== "scroll") ripples.push({ x: at.x, y: at.y, born: now, color });
    if (event.label) chips.push({ x: at.x, y: at.y, born: now, text: event.label, color });
    if (mode === "replay" && (event.kind === "click" || event.kind === "rage" || event.kind === "conversion")) {
      heat.push({
        x: event.x,
        y: event.y,
        strength: event.kind === "rage" ? 1.15 : 0.85,
        rgb: event.kind === "rage" ? "255, 90, 60" : "200, 240, 106",
      });
    }
  };

  const takeOver = () => {
    if (userTook) return;
    userTook = true;
    events = events.filter((event) => event.t < recT);
  };

  const field = (x: number, y: number, t: number) =>
    Math.sin(x * 0.0038 + t * 0.00034) * Math.cos(y * 0.0046 - t * 0.00025) * 3.2 +
    Math.sin((x + y) * 0.0017 + t * 0.00047) * 2.3;

  const tapeTime = (name: ReelPhase, local: number, length: number) => {
    if (name === "rec" || name === "replay") return local;
    if (name === "rewind") return TAPE_MS * (1 - easeInOut(local / length));
    if (name === "insight" || name === "agent" || name === "verify") return TAPE_MS;
    return 0;
  };

  const pointerInPage = () =>
    pointer.active && pointer.x >= page.x && pointer.x <= page.x + page.w && pointer.y >= page.y && pointer.y <= page.y + page.h;

  const tick = (now: number) => {
    rafId = requestAnimationFrame(tick);
    let loopMs = now - loopStart;
    if (loopMs >= REEL_LOOP_MS) {
      loopStart = now;
      loopMs = 0;
    }
    const [name, local, length] = phaseAt(Math.max(0, loopMs));
    if (name !== phase) {
      phase = name;
      if (name === "rec") {
        samples = [];
        events = [];
        userTook = false;
        heat.length = 0;
        chips.length = 0;
      }
      if (name === "replay") {
        replayedUpTo = -1;
        heat.length = 0;
        chips.length = 0;
      }
      if (name === "verify") verifyClicks = 0;
      onPhase?.(name);
    }
    const T = tapeTime(name, local, length);
    const onTape = name === "rec" || name === "rewind" || name === "replay" || name === "insight";
    // ページの状態(入力済み・スクロール・申込済み)は、エージェントと確認の段でも残す
    const pageState = onTape || name === "agent" || name === "verify";
    const afterFix = name === "verify";

    // 記録
    if (name === "rec") {
      recT = T;
      if (!userTook && pointerInPage() && now - pointer.lastMove < 400) takeOver();
      const at = userTook
        ? { x: clamp01((pointer.x - page.x) / page.w), y: clamp01((pointer.y - page.y) / page.h) }
        : scripted(T);
      samples.push({ t: T, x: at.x, y: at.y });
      if (!userTook) {
        for (const event of scriptedEvents(labels)) {
          if (event.t <= T && !events.some((e) => e.t === event.t)) {
            events.push(event);
            emit(event, now, "rec");
          }
        }
      }
    }
    // 再生
    if (name === "replay") {
      for (const event of events) {
        if (event.t > replayedUpTo && event.t <= T) emit(event, now, "replay");
      }
      replayedUpTo = T;
    }

    const cursor = onTape ? interpolate(samples, T) : null;
    const cursorPx = cursor ? toPx(cursor.x, cursor.y) : null;

    // ---- 粒子(ページの線画) ----
    const forming = name === "form";
    const dissolving = name === "dissolve";
    const formProgress = forming ? easeOutCubic(local / length) : 1;
    const stiffness = 0.008 + formProgress * 0.05;
    const wobble = name === "rewind" ? Math.sin((local / length) * Math.PI) : 0;
    const cx = box.x + box.w / 2;
    const cy = box.y + box.h / 2;
    const radius = width < 820 ? 60 : 100;
    const repelPointer = pointer.active && !(name === "rec" && userTook);

    for (let i = 0; i < count; i += 1) {
      const angle = field(xs[i], ys[i], now);
      let ax = Math.cos(angle);
      let ay = Math.sin(angle);
      let damping = 0.95;
      if (targeted[i] && !dissolving) {
        // 巻き戻し中はVHSのように横へ揺らす
        const band = Math.sin(tys[i] * 0.09 + now * 0.03) * Math.sin(tys[i] * 0.013 + now * 0.007);
        const tx = txs[i] + band * 38 * wobble;
        const flowShare = forming ? (1 - formProgress) * 0.5 : 0;
        ax = ax * flowShare + (tx - xs[i]) * stiffness;
        ay = ay * flowShare + (tys[i] - ys[i]) * stiffness;
        damping = 0.84;
      } else if (targeted[i]) {
        const burst = local < 34 ? 0.02 : 0;
        ax = ax * 0.55 + (xs[i] - cx) * burst;
        ay = ay * 0.55 + (ys[i] - cy) * burst;
        damping = 0.955;
      } else {
        ax *= 0.1;
        ay *= 0.1;
      }
      vxs[i] = vxs[i] * damping + ax;
      vys[i] = vys[i] * damping + ay;
      if (repelPointer) {
        const dx = xs[i] - pointer.x;
        const dy = ys[i] - pointer.y;
        const d = Math.hypot(dx, dy);
        if (d < radius && d > 0.01) {
          vxs[i] += (dx / d) * (1 - d / radius) * 2.2;
          vys[i] += (dy / d) * (1 - d / radius) * 2.2;
        }
      }
      xs[i] += vxs[i];
      ys[i] += vys[i];
      if (!targeted[i]) {
        if (xs[i] < -10) xs[i] = width + 10;
        else if (xs[i] > width + 10) xs[i] = -10;
        if (ys[i] < -10) ys[i] = height + 10;
        else if (ys[i] > height + 10) ys[i] = -10;
      }
    }

    trails.fillStyle = WIPE;
    trails.fillRect(0, 0, width, height);
    const dim = name === "insight" || name === "agent" || name === "verify" ? 0.42 : 1;
    for (let c = 0; c < PALETTE.length; c += 1) {
      trails.fillStyle = name === "rewind" && c === 0 ? AMBER : PALETTE[c];
      trails.globalAlpha = 0.85 * dim;
      for (let i = 0; i < count; i += 1) {
        if (colors[i] !== c) continue;
        trails.fillRect(xs[i] - sizes[i] / 2, ys[i] - sizes[i] / 2, sizes[i], sizes[i]);
      }
    }
    trails.globalAlpha = 1;

    // ---- 重ね描き ----
    ctx.clearRect(0, 0, width, height);
    const labelAlpha = forming ? clamp01((local - length * 0.5) / (length * 0.5)) : dissolving ? 1 - local / length : 1;
    ctx.globalAlpha = labelAlpha;
    ctx.textBaseline = "middle";

    // ページ上の文字と状態
    const url = rectPx(URL_BAR);
    ctx.textAlign = "start";
    ctx.font = `600 ${Math.max(9, page.h * 0.024)}px ${MONO}`;
    ctx.fillStyle = "rgba(228, 244, 212, 0.62)";
    ctx.fillText(labels.url, url.x + 10, url.y + url.h / 2);
    ctx.textAlign = "center";
    ctx.font = `700 ${Math.max(10, page.h * 0.028)}px ${SANS}`;
    NAV.forEach((item, index) => {
      const r = rectPx(item);
      ctx.fillStyle = index === 1 ? LIME : "rgba(228, 244, 212, 0.75)";
      ctx.fillText(labels.nav[index], r.x + r.w / 2, r.y + r.h / 2 + 1);
    });
    const ctaBox = rectPx(CTA);
    const converted = pageState && T >= 4750 && has("conversion");
    ctx.fillStyle = converted ? "rgba(111, 224, 168, 0.92)" : "rgba(200, 240, 106, 0.16)";
    ctx.fillRect(ctaBox.x + 2, ctaBox.y + 2, ctaBox.w - 4, ctaBox.h - 4);
    ctx.fillStyle = converted ? BG : LIME;
    ctx.fillText(converted ? `✓ ${labels.cta}` : labels.cta, ctaBox.x + ctaBox.w / 2, ctaBox.y + ctaBox.h / 2 + 1);
    const toggleBox = rectPx(TOGGLE);
    ctx.font = `600 ${Math.max(9, page.h * 0.022)}px ${SANS}`;
    ctx.fillStyle = "rgba(228, 244, 212, 0.8)";
    ctx.fillText(labels.toggle, toggleBox.x + toggleBox.w / 2, toggleBox.y + toggleBox.h / 2 + 1);
    ctx.textAlign = "start";
    ctx.font = `800 ${Math.max(10, page.h * 0.03)}px ${SANS}`;
    CARDS.forEach((card, index) => {
      const r = rectPx(card);
      ctx.fillStyle = "rgba(228, 244, 212, 0.85)";
      ctx.fillText(labels.plans[index], r.x + r.w * 0.1, r.y + r.h * (index === 1 ? 0.37 : 0.22));
    });
    // 入力欄(伏せ字)
    const typed = pageState && has("input") ? Math.round(clamp01((T - 2450) / 650) * 9) : 0;
    const inputBox = rectPx(INPUT);
    ctx.font = `700 ${Math.max(12, page.h * 0.034)}px ${MONO}`;
    ctx.fillStyle = INK;
    ctx.fillText("•".repeat(typed), inputBox.x + 12, inputBox.y + inputBox.h / 2 + 1);
    ctx.textAlign = "end";
    ctx.font = `800 ${Math.max(8, page.h * 0.019)}px ${MONO}`;
    ctx.fillStyle = AMBER;
    ctx.fillText("MASKED", inputBox.x + inputBox.w - 10, inputBox.y + inputBox.h / 2 + 1);
    // スクロールバー
    const scrollP = pageState && has("scroll") ? clamp01((T - 1450) / 450) : 0;
    const trackX = page.x + page.w - 8;
    const trackTop = page.y + page.h * 0.09;
    const trackH = page.h * 0.86;
    ctx.fillStyle = "rgba(228, 244, 212, 0.1)";
    ctx.fillRect(trackX, trackTop, 3, trackH);
    ctx.fillStyle = MINT;
    ctx.fillRect(trackX, trackTop + trackH * 0.42 * scrollP, 3, trackH * 0.3);

    // ヒートマップ(再生でクリック地点が温まる)
    if (heat.length) {
      ctx.globalCompositeOperation = "lighter";
      const glow = name === "insight" || name === "agent" ? 1 : 0.7;
      // 直したあとは、連打の赤い熱が引いていく
      const cool = afterFix ? 1 - easeInOut(clamp01(local / 1600)) : 1;
      for (const spot of heat) {
        const at = toPx(spot.x, spot.y);
        const fade = spot.rgb.startsWith("255") ? cool : 1;
        if (fade <= 0.01) continue;
        const r = Math.min(page.w, page.h) * 0.13 * spot.strength * (0.4 + 0.6 * fade);
        const gradient = ctx.createRadialGradient(at.x, at.y, 0, at.x, at.y, r);
        gradient.addColorStop(0, `rgba(${spot.rgb}, ${(0.42 * glow * fade * labelAlpha).toFixed(3)})`);
        gradient.addColorStop(1, `rgba(${spot.rgb}, 0)`);
        ctx.fillStyle = gradient;
        ctx.fillRect(at.x - r, at.y - r, r * 2, r * 2);
      }
      ctx.globalCompositeOperation = "source-over";
    }

    // 軌跡
    if (cursor && samples.length > 1 && name !== "insight") {
      ctx.lineWidth = 1.8;
      ctx.setLineDash(name === "rewind" ? [5, 5] : []);
      ctx.strokeStyle = name === "rec" ? "rgba(255, 90, 60, 0.8)" : name === "rewind" ? AMBER : "rgba(111, 224, 168, 0.9)";
      ctx.beginPath();
      let started = false;
      const from = name === "rec" ? T - 1400 : -1;
      for (const s of samples) {
        if (s.t > T) break;
        if (s.t < from) continue;
        const p = toPx(s.x, s.y);
        if (started) ctx.lineTo(p.x, p.y);
        else ctx.moveTo(p.x, p.y);
        started = true;
      }
      if (started && cursorPx) ctx.lineTo(cursorPx.x, cursorPx.y);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // 波紋
    for (let i = ripples.length - 1; i >= 0; i -= 1) {
      const age = (now - ripples[i].born) / 800;
      if (age >= 1) {
        ripples.splice(i, 1);
        continue;
      }
      ctx.strokeStyle = ripples[i].color;
      ctx.globalAlpha = labelAlpha * (1 - age);
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(ripples[i].x, ripples[i].y, 6 + easeOutCubic(age) * 34, 0, Math.PI * 2);
      ctx.stroke();
    }
    ctx.globalAlpha = labelAlpha;

    // カーソル
    if (cursorPx && name !== "insight") {
      ctx.textAlign = "start";
      ctx.font = `700 10px ${MONO}`;
      if (name === "rec") {
        if (!userTook) drawArrow(ctx, cursorPx.x, cursorPx.y, INK, BG);
        ctx.fillStyle = RED;
        ctx.beginPath();
        ctx.arc(cursorPx.x + 20, cursorPx.y + 27, 3.5, 0, Math.PI * 2);
        ctx.fill();
        ctx.fillStyle = INK;
        ctx.fillText(`${labels.recording} · ${userTook ? labels.you : labels.visitor}`, cursorPx.x + 28, cursorPx.y + 27);
      } else {
        const color = name === "rewind" ? AMBER : MINT;
        drawArrow(ctx, cursorPx.x, cursorPx.y, "rgba(8, 16, 12, 0.6)", color);
        ctx.fillStyle = color;
        ctx.fillText(
          name === "rewind" ? labels.rewind : `${labels.replay} · ${userTook ? labels.you : labels.visitor}`,
          cursorPx.x + 18,
          cursorPx.y + 27,
        );
      }
    }

    // イベントのラベル
    ctx.font = `700 11px ${MONO}`;
    ctx.textAlign = "start";
    for (let i = chips.length - 1; i >= 0; i -= 1) {
      const chip = chips[i];
      const age = (now - chip.born) / 1700;
      if (age >= 1) {
        chips.splice(i, 1);
        continue;
      }
      const alpha = age < 0.1 ? age / 0.1 : age > 0.75 ? (1 - age) / 0.25 : 1;
      const w = ctx.measureText(chip.text).width + 18;
      let x = chip.x + 16;
      const y = chip.y - 36 - age * 10;
      if (x + w > page.x + page.w - 12) x = chip.x - w - 12;
      ctx.globalAlpha = labelAlpha * alpha;
      ctx.fillStyle = "rgba(8, 16, 12, 0.9)";
      pill(ctx, x, y, w, 22);
      ctx.fill();
      ctx.strokeStyle = chip.color;
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = chip.color;
      ctx.fillText(chip.text, x + 9, y + 11.5);
    }
    ctx.globalAlpha = labelAlpha;

    // 発見: 迷った場所と、申込に至った場所を囲む
    if (name === "insight") {
      const show = easeOutCubic(clamp01(local / 500));
      const callouts: [Rect, string, string, boolean][] = [];
      if (has("rage")) callouts.push([TOGGLE, labels.insightRage, RED, false]);
      if (has("conversion")) callouts.push([CTA, labels.insightConversion, MINT, true]);
      if (callouts.length === 0 && events.length > 0) {
        const last = events[events.length - 1];
        callouts.push([[last.x - 0.05, last.y - 0.04, 0.1, 0.08], labels.click, MINT, true]);
      }
      for (const [rect, text, color, above] of callouts) {
        const r = rectPx(rect);
        const pad = 6 + (1 - show) * 30;
        ctx.globalAlpha = labelAlpha * show;
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(r.x - pad, r.y - pad, r.w + pad * 2, r.h + pad * 2);
        ctx.setLineDash([]);
        ctx.font = `800 12px ${SANS}`;
        const w = ctx.measureText(text).width + 20;
        const tx = Math.max(page.x + 8, Math.min(r.x - 6, page.x + page.w - w - 14));
        const ty = above ? r.y - 44 : r.y + r.h + 14;
        ctx.fillStyle = color;
        pill(ctx, tx, ty, w, 26);
        ctx.fill();
        ctx.fillStyle = BG;
        ctx.fillText(text, tx + 10, ty + 13.5);
      }
      ctx.globalAlpha = labelAlpha;
    }

    // エージェントが直す: 直す場所に印
    if (name === "agent" || name === "verify") {
      const r = rectPx(TOGGLE);
      const fixed = name === "verify" || local >= labels.agentFixAt;
      ctx.globalAlpha = labelAlpha;
      if (name === "agent" && !fixed) {
        const pad = 6;
        ctx.strokeStyle = RED;
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 4]);
        ctx.strokeRect(r.x - pad, r.y - pad, r.w + pad * 2, r.h + pad * 2);
        ctx.setLineDash([]);
      } else if (fixed) {
        const since = name === "verify" ? 1_000 : local - labels.agentFixAt;
        const pulse = name === "agent" ? 0.5 + 0.5 * Math.sin(since / 120) : 1;
        ctx.strokeStyle = name === "verify" ? MINT : LIME;
        ctx.lineWidth = 2 + pulse;
        ctx.strokeRect(r.x - 6, r.y - 6, r.w + 12, r.h + 12);
        ctx.font = `800 11px ${MONO}`;
        ctx.fillStyle = name === "verify" ? MINT : LIME;
        ctx.textAlign = "end";
        ctx.fillText(name === "verify" ? labels.verifyCallout : "FIX", r.x + r.w + 6, r.y - 14);
        ctx.textAlign = "start";
      }
    }

    // 確認: 直したあとの訪問者は、切替を1回押して申込へ進む
    if (name === "verify") {
      const path: [number, number, number][] = [
        [0, 0.9, 0.95],
        [1200, TOGGLE_AT.x, TOGGLE_AT.y],
        [1500, TOGGLE_AT.x, TOGGLE_AT.y],
        [2500, CTA_AT.x, CTA_AT.y],
        [3800, CTA_AT.x, CTA_AT.y],
      ];
      let at = { x: path[0]![1], y: path[0]![2] };
      for (let i = 1; i < path.length; i += 1) {
        if (local <= path[i]![0]) {
          const a = path[i - 1]!;
          const b = path[i]!;
          const p = easeInOut(clamp01((local - a[0]) / (b[0] - a[0])));
          at = { x: a[1] + (b[1] - a[1]) * p, y: a[2] + (b[2] - a[2]) * p };
          break;
        }
        at = { x: path[i]![1], y: path[i]![2] };
      }
      const px = toPx(at.x, at.y);
      if (verifyClicks === 0 && local >= 1250) {
        verifyClicks = 1;
        ripples.push({ born: now, color: MINT, x: px.x, y: px.y });
      }
      if (verifyClicks === 1 && local >= 2550) {
        verifyClicks = 2;
        ripples.push({ born: now, color: MINT, x: px.x, y: px.y });
      }
      drawArrow(ctx, px.x, px.y, INK, BG);
    }

    // ターミナル(エージェントがFlowTraceを読んで直す)
    if (name === "agent" || name === "verify") {
      const narrow = page.w < 600;
      const term = narrow
        ? { h: page.h * 0.55, w: page.w * 0.96, x: page.x + page.w * 0.02, y: page.y + page.h * 0.43 }
        : { h: page.h * 0.88, w: page.w * 0.37, x: page.x + page.w * 0.015, y: page.y + page.h * 0.09 };
      const slide = name === "agent" ? easeOutCubic(clamp01(local / 420)) : 1;
      ctx.globalAlpha = labelAlpha * slide;
      const offsetX = (1 - slide) * -24;
      ctx.fillStyle = "rgba(3, 8, 5, 0.94)";
      pill(ctx, term.x + offsetX, term.y, term.w, term.h);
      ctx.fill();
      ctx.strokeStyle = "rgba(111, 224, 168, 0.55)";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = "rgba(228, 244, 212, 0.25)";
      for (let i = 0; i < 3; i += 1) {
        ctx.beginPath();
        ctx.arc(term.x + offsetX + 14 + i * 11, term.y + 14, 3, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.font = `700 10px ${MONO}`;
      ctx.fillStyle = "rgba(228, 244, 212, 0.55)";
      ctx.fillText(labels.agentTitle, term.x + offsetX + 52, term.y + 15);
      const toneColor: Record<ReelTerminalTone, string> = {
        bad: RED,
        call: LIME,
        dim: "rgba(228, 244, 212, 0.6)",
        good: MINT,
        prompt: INK,
        result: INK,
      };
      const shown: { text: string; tone: ReelTerminalTone; done: boolean }[] = [];
      const pushLines = (lines: ReelTerminalLine[], elapsed: number) => {
        for (const line of lines) {
          if (elapsed < line.at) break;
          const chars = Math.floor((elapsed - line.at) / 16);
          shown.push({ done: chars >= line.text.length, text: line.text.slice(0, chars), tone: line.tone });
        }
      };
      pushLines(labels.agentLines, name === "agent" ? local : Number.POSITIVE_INFINITY);
      if (name === "verify") pushLines(labels.verifyLines, local);
      const lineH = 18;
      const top = term.y + 36;
      const maxLines = Math.max(1, Math.floor((term.h - 46) / lineH));
      const visible = shown.slice(-maxLines);
      ctx.font = `600 ${narrow ? 10 : 11}px ${MONO}`;
      ctx.save();
      ctx.beginPath();
      ctx.rect(term.x + offsetX + 8, term.y + 26, term.w - 16, term.h - 30);
      ctx.clip();
      visible.forEach((line, index) => {
        ctx.fillStyle = toneColor[line.tone];
        ctx.fillText(line.text, term.x + offsetX + 12, top + index * lineH);
        const last = index === visible.length - 1;
        if (last && (!line.done || Math.floor(now / 450) % 2 === 0)) {
          const w = ctx.measureText(line.text).width;
          ctx.fillStyle = LIME;
          ctx.fillRect(term.x + offsetX + 13 + w, top + index * lineH - 6, 6, 12);
        }
      });
      ctx.restore();
      ctx.globalAlpha = labelAlpha;
    }

    // リプレイのタイムライン
    const tl = { x: page.x + 4, y: page.y + page.h + 24, w: page.w - 140 };
    ctx.fillStyle = "rgba(228, 244, 212, 0.14)";
    ctx.fillRect(tl.x, tl.y, tl.w, 4);
    if (pageState) {
      for (const event of events) {
        const x = tl.x + (event.t / TAPE_MS) * tl.w;
        const lit = name === "rec" || !onTape || name === "insight" || event.t <= T;
        ctx.fillStyle = event.kind === "rage" ? RED : event.kind === "input" ? AMBER : event.kind === "conversion" ? MINT : LIME;
        ctx.globalAlpha = labelAlpha * (lit ? 1 : 0.3);
        if (event.kind === "conversion") {
          ctx.beginPath();
          ctx.arc(x, tl.y + 2, 5, 0, Math.PI * 2);
          ctx.fill();
        } else ctx.fillRect(x - 1, tl.y - 6, 2, 16);
      }
      ctx.globalAlpha = labelAlpha;
      const headX = tl.x + (T / TAPE_MS) * tl.w;
      const headColor = name === "rec" ? RED : name === "rewind" ? AMBER : MINT;
      ctx.fillStyle = headColor;
      ctx.fillRect(tl.x, tl.y, headX - tl.x, 4);
      ctx.beginPath();
      ctx.arc(headX, tl.y + 2, 6, 0, Math.PI * 2);
      ctx.fillStyle = BG;
      ctx.fill();
      ctx.strokeStyle = headColor;
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    ctx.font = `700 11px ${MONO}`;
    ctx.textAlign = "end";
    ctx.fillStyle = INK;
    ctx.fillText(`00:${(T / 1000).toFixed(1).padStart(4, "0")} / 00:0${(TAPE_MS / 1000).toFixed(1)}`, page.x + page.w - 4, tl.y + 3);
    ctx.textAlign = "start";
    ctx.globalAlpha = 1;
  };

  const start = () => {
    if (running || document.hidden) return;
    running = true;
    rafId = requestAnimationFrame(tick);
  };
  const stop = () => {
    running = false;
    cancelAnimationFrame(rafId);
  };

  const handleMove = (event: PointerEvent) => {
    const rect = host.getBoundingClientRect();
    pointer.x = event.clientX - rect.left;
    pointer.y = event.clientY - rect.top;
    pointer.active = true;
    pointer.lastMove = performance.now();
  };
  const handleLeave = () => {
    pointer.active = false;
  };
  const handleDown = (event: PointerEvent) => {
    if ((event.target as Element | null)?.closest("a, button, input, .login-card, .login-topbar")) return;
    handleMove(event);
    if (phase !== "rec" || !pointerInPage()) return;
    takeOver();
    const tapeEvent: TapeEvent = {
      t: recT,
      kind: "click",
      x: (pointer.x - page.x) / page.w,
      y: (pointer.y - page.y) / page.h,
      label: labels.click,
    };
    events.push(tapeEvent);
    emit(tapeEvent, performance.now(), "rec");
  };
  const handleVisibility = () => (document.hidden ? stop() : start());

  measure();
  start();
  onPhase?.("form");
  const resizeObserver = new ResizeObserver(() => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(measure);
  });
  resizeObserver.observe(host);
  resizeObserver.observe(frame);
  host.addEventListener("pointermove", handleMove);
  host.addEventListener("pointerleave", handleLeave);
  host.addEventListener("pointerdown", handleDown);
  document.addEventListener("visibilitychange", handleVisibility);

  return {
    destroy: () => {
      stop();
      cancelAnimationFrame(resizeFrame);
      resizeObserver.disconnect();
      host.removeEventListener("pointermove", handleMove);
      host.removeEventListener("pointerleave", handleLeave);
      host.removeEventListener("pointerdown", handleDown);
      document.removeEventListener("visibilitychange", handleVisibility);
    },
  };
};
